<!-- src/components/SelectCategory.vue -->
<template>
  <div class="w-full">
    <label v-if="label" class="block text-sm font-medium text-gray-700 mb-1">
      {{ label }}
    </label>

    <!-- Optional search box -->
    <input
      v-if="showSearch"
      type="text"
      :placeholder="searchPlaceholder"
      class="ts-field mb-2"
      v-model="query"
      :disabled="disabled || loading"
      aria-label="Search categories"
    />

    <select
      :class="['ts-field','ts-select', inputClass]"
      :value="modelValue"
      @change="onChange"
      :disabled="disabled || loading"
      aria-label="Select category"
    >
      <option :value="''" disabled>
        {{ loading ? 'Loading…' : placeholder }}
      </option>

      <!-- Preserve pre-set value not in options yet (e.g., while state switching) -->
      <option v-if="modelValue && !optionsList.includes(modelValue)" :value="modelValue">
        {{ modelValue }}
      </option>

      <option v-for="c in filteredOptions" :key="c" :value="c">
        {{ c }}
      </option>
    </select>

    <p v-if="loading" class="mt-1 text-xs text-gray-500">Loading categories…</p>
    <p v-if="!loading && filteredOptions.length === 0" class="mt-1 text-xs text-gray-500">
      No categories found.
    </p>
    <p v-if="error" class="mt-1 text-xs text-red-600">{{ error }}</p>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue'
import { useGstRatesStore } from '@/stores/gstRates.js'

export default {
  name: 'SelectCategory',
  props: {
    modelValue: { type: String, default: '' },
    label: { type: String, default: 'Category' },
    placeholder: { type: String, default: 'Select a category' },
    disabled: { type: Boolean, default: false },

    /**
     * If provided, these options are used directly (store is skipped).
     * Useful for custom or pre-filtered lists.
     */
    options: { type: Array, default: () => [] },

    /** Auto-select first item when the list is loaded and v-model is empty */
    autoSelectFirst: { type: Boolean, default: false },

    /** Show a client-side search input */
    showSearch: { type: Boolean, default: true },
    searchPlaceholder: { type: String, default: 'Search categories…' },

    /**
     * Supplier state (used to filter categories via the store).
     * When empty, we fall back to all categories from the store.
     */
    state: { type: String, default: '' },

    /** Optional extra classes to append to the <select> */
    inputClass: { type: String, default: '' }
  },
  emits: ['update:modelValue'],

  setup(props, { emit }) {
    const gst = useGstRatesStore()

    // With static JSON in the store, there's no real loading here.
    const loading = ref(false)
    const error = ref('')
    const query = ref('')

    // Source list priority:
    // 1) props.options (if provided & non-empty)
    // 2) store.categoriesByState(props.state) (if state provided)
    // 3) store.allCategories (fallback)
    const baseOptions = computed(() => {
      if (props.options && props.options.length) return props.options
      if (props.state) return gst.categoriesByState(props.state)
      return gst.allCategories
    })

    const optionsList = computed(() => {
      const unique = Array.from(new Set(baseOptions.value))
      return unique.sort((a, b) => String(a).localeCompare(String(b)))
    })

    const filteredOptions = computed(() => {
      const q = query.value.trim().toLowerCase()
      if (!q) return optionsList.value
      return optionsList.value.filter(c => String(c).toLowerCase().includes(q))
    })

    function onChange(e) {
      emit('update:modelValue', e.target.value)
    }

    // Auto-select first option when list becomes available or state changes
    watch(
      () => [optionsList.value, props.state, props.options],
      () => {
        if (props.autoSelectFirst && !props.modelValue && optionsList.value.length) {
          emit('update:modelValue', optionsList.value[0])
        }
        // Optional: clear value if it no longer exists in options
        // else if (props.modelValue && !optionsList.value.includes(props.modelValue)) {
        //   emit('update:modelValue', '')
        // }
      },
      { immediate: true, deep: true }
    )

    return { loading, error, query, optionsList, filteredOptions, onChange }
  }
}
</script>

<style scoped>
/* Relies on global Tailwind + @tailwindcss/forms + .ts-field/.ts-select in style.css */
</style>
